import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    console.log('[VariantPicker] connectedCallback', {
      element: this,
      hasChangeListeners: true,
      templateProductMatch: this.dataset.templateProductMatch,
      sectionId: this.dataset.sectionId,
      productId: this.dataset.productId,
      hasForm: !!this.querySelector('form'),
      radioInputs: this.querySelectorAll('input[type="radio"]').length,
      selectInputs: this.querySelectorAll('select').length
    });

    // Also listen for clicks on labels/inputs to catch any missed events
    this.addEventListener('click', (event) => {
      console.log('[VariantPicker] click event received', {
        target: event.target,
        targetTag: event.target?.tagName,
        isInput: event.target instanceof HTMLInputElement,
        isLabel: event.target instanceof HTMLLabelElement,
        relatedInput: event.target instanceof HTMLLabelElement ? this.querySelector(`input#${event.target.getAttribute('for')}`) : null
      });
    });

    this.addEventListener('change', (event) => {
      console.log('[VariantPicker] change event received', {
        target: event.target,
        targetType: event.target?.tagName,
        targetValue: event.target?.value,
        variantId: event.target?.dataset?.variantId || event.target?.options?.[event.target?.selectedIndex]?.dataset?.variantId
      });
      this.variantChanged(event);
    });

    // Debug: Log all radio inputs to see their structure
    const radioInputs = this.querySelectorAll('input[type="radio"]');
    console.log('[VariantPicker] Found radio inputs', {
      count: radioInputs.length,
      inputs: Array.from(radioInputs).map(input => ({
        name: input.name,
        value: input.value,
        variantId: input.dataset.variantId,
        optionValueId: input.dataset.optionValueId,
        checked: input.checked
      }))
    });

    // Add direct click handlers to radio inputs as a fallback
    radioInputs.forEach(input => {
      input.addEventListener('click', (e) => {
        console.log('[VariantPicker] Radio input clicked directly', {
          input: e.target,
          variantId: e.target.dataset.variantId,
          optionValueId: e.target.dataset.optionValueId,
          value: e.target.value
        });
        // Manually trigger change event if needed
        setTimeout(() => {
          if (e.target.checked) {
            console.log('[VariantPicker] Radio is now checked, triggering change');
            e.target.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }, 0);
      });
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    console.log('[VariantPicker] variantChanged called', {
      event,
      target: event.target,
      isHTMLElement: event.target instanceof HTMLElement
    });
    
    if (!(event.target instanceof HTMLElement)) {
      console.warn('[VariantPicker] variantChanged - target is not HTMLElement', event.target);
      return;
    }

    console.log('[VariantPicker] variantChanged - updating selected option', {
      target: event.target,
      optionValueId: event.target.dataset.optionValueId,
      variantId: event.target.dataset.variantId || event.target.options?.[event.target.selectedIndex]?.dataset?.variantId
    });

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);

    let variantId;

    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      variantId = event.target.dataset.variantId || null;
    } else if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    // Get variant ID from the selected option if available
    let variantId = null;
    if (selectedOption instanceof HTMLInputElement && selectedOption.type === 'radio') {
      variantId = selectedOption.dataset.variantId || null;
    } else if (selectedOption instanceof HTMLSelectElement) {
      const selectedOptionElement = selectedOption.options[selectedOption.selectedIndex];
      variantId = selectedOptionElement?.dataset.variantId || null;
    } else if (selectedOption instanceof HTMLElement) {
      variantId = selectedOption.dataset.variantId || null;
    }

    // For product pages with a valid variant ID, use variant parameter instead of option_values
    // This ensures Shopify's selected_or_first_available_variant is properly updated
    const isOnProductPage = this.dataset.templateProductMatch === 'true';
    if (isOnProductPage && variantId && !source) {
      // Use variant ID parameter for product pages - this is more reliable for updating variant context
      params.push(`variant=${variantId}`);
    } else {
      // Use option_values for other cases
      if (this.selectedOptionsValues.length && !source) {
        params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
      } else if (source === 'product-card') {
        if (this.selectedOptionsValues.length) {
          params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
        } else {
          params.push(`option_values=${selectedOption.dataset.optionValueId}`);
        }
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    // For product pages, include section_id to ensure Shopify updates the variant context properly
    // This ensures that selected_or_first_available_variant is updated in Liquid, which affects pricing
    const sectionId = this.dataset.sectionId;
    if (sectionId && isOnProductPage) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      const sectionParam = `section_id=${sectionId}`;
      const allParams = params.length > 0 ? [sectionParam, ...params] : [sectionParam];
      return `${productUrl}?${allParams.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        console.log('[VariantPicker] Fetched response', {
          requestUrl,
          responseLength: responseText.length,
          responsePreview: responseText.substring(0, 1000),
          hasProductPrice: responseText.includes('product-price'),
          hasPriceContainer: responseText.includes('priceContainer')
        });
        
        this.#pendingRequestUrl = undefined;
        // When using section_id, Shopify returns just the section HTML, not a full page
        // Parse it appropriately - if it starts with a section element, parse as HTML fragment
        let html;
        const trimmedResponse = responseText.trim();
        if (trimmedResponse.startsWith('<') && (trimmedResponse.includes('<div') || trimmedResponse.includes('<section'))) {
          // Section HTML fragment - wrap it to create a valid document
          html = new DOMParser().parseFromString(`<html><body>${responseText}</body></html>`, 'text/html');
        } else {
          // Full page HTML
          html = new DOMParser().parseFromString(responseText, 'text/html');
        }
        
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        console.log('[VariantPicker] Variant data from response', {
          hasVariantData: !!textContent,
          variantData: textContent ? JSON.parse(textContent) : null,
          priceElementsInResponse: Array.from(html.querySelectorAll('product-price')).length,
          priceElementsDetails: Array.from(html.querySelectorAll('product-price')).map(el => ({
            productId: el.dataset.productId,
            blockId: el.dataset.blockId,
            priceText: el.querySelector('[ref="priceContainer"]')?.textContent?.trim()
          }))
        });
        
        if (!textContent) {
          console.warn('[VariantPicker] No variant data found in response');
          return;
        }

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
  console.log('[VariantPicker] Custom element registered');
  
  // Check if any variant-picker elements already exist in the DOM
  const existingElements = document.querySelectorAll('variant-picker');
  console.log('[VariantPicker] Checking for existing elements in DOM', {
    count: existingElements.length,
    elements: Array.from(existingElements).map(el => ({
      hasForm: !!el.querySelector('form'),
      sectionId: el.dataset.sectionId,
      productId: el.dataset.productId,
      isConnected: el.isConnected
    }))
  });
  
  // Set up MutationObserver to detect when variant-picker elements are added to DOM
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          // Check if the added node is a variant-picker
          if (node.tagName === 'VARIANT-PICKER' || node.tagName?.toLowerCase() === 'variant-picker') {
            console.log('[VariantPicker] Element detected being added to DOM', {
              element: node,
              sectionId: node.dataset.sectionId,
              productId: node.dataset.productId,
              hasForm: !!node.querySelector('form')
            });
          }
          // Check if any variant-picker is added within the node
          const variantPickers = node.querySelectorAll?.('variant-picker');
          if (variantPickers && variantPickers.length > 0) {
            console.log('[VariantPicker] Variant picker elements found in added node', {
              count: variantPickers.length,
              elements: Array.from(variantPickers).map(el => ({
                sectionId: el.dataset.sectionId,
                productId: el.dataset.productId,
                hasForm: !!el.querySelector('form')
              }))
            });
          }
        }
      });
    });
  });
  
  // Start observing
  observer.observe(document.body || document.documentElement, {
    childList: true,
    subtree: true
  });
  
  // Also check after delays in case elements are added dynamically
  setTimeout(() => {
    const delayedElements = document.querySelectorAll('variant-picker');
    console.log('[VariantPicker] Delayed check (1s) for elements', {
      count: delayedElements.length,
      elements: Array.from(delayedElements).map(el => ({
        hasForm: !!el.querySelector('form'),
        sectionId: el.dataset.sectionId,
        productId: el.dataset.productId,
        isConnected: el.isConnected
      }))
    });
  }, 1000);
  
  setTimeout(() => {
    const delayedElements = document.querySelectorAll('variant-picker');
    console.log('[VariantPicker] Delayed check (3s) for elements', {
      count: delayedElements.length,
      elements: Array.from(delayedElements).map(el => ({
        hasForm: !!el.querySelector('form'),
        sectionId: el.dataset.sectionId,
        productId: el.dataset.productId,
        isConnected: el.isConnected
      }))
    });
  }, 3000);
} else {
  console.log('[VariantPicker] Custom element already registered');
}
